import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { exchangeKCCode, exchangeKCTokenForRonspot } from './keycloakAuth';
import { CheckUserVehicle_api } from '../Login/LoginAPI';
import { persistSession } from '../../utils/SecurityService';
import { Images } from '../Common/Images';

// Verbose on-screen sign-in diagnostics (failure step / message / raw details) are for staging
// bring-up only — they can expose server/internal detail. Gated to non-production builds:
// REACT_APP_KC_DIAGNOSTICS=true in .env(.staging); absent in .env.production → generic error for users.
const SHOW_DIAGNOSTICS = process.env.REACT_APP_KC_DIAGNOSTICS === 'true';

export default function KeycloakCallback() {
  const history = useHistory();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error');

    if (errorParam) {
      setError({
        step: 'Identity provider redirect',
        message: params.get('error_description') || errorParam,
        details: { error: errorParam, error_description: params.get('error_description') },
      });
      return;
    }

    if (!code) {
      history.replace('/login');
      return;
    }

    let cancelled = false;

    // NOTE: verbose on-screen diagnostics are intentional for the staging bring-up
    // of the Keycloak flow. Gate/remove before this flow goes to production.
    (async () => {
      let step = 'Keycloak token exchange';
      try {
        // Exchange the KC auth code for tokens, then swap the id_token for a
        // Ronspot session. id_token is always a signed JWT (unlike the
        // public-client access_token, which can be opaque).
        const kcTokens = await exchangeKCCode(code, state);

        step = 'id_token check';
        if (!kcTokens.id_token) {
          const e = new Error('Keycloak response did not include an id_token.');
          e.details = { tokenKeys: Object.keys(kcTokens || {}) };
          throw e;
        }

        step = 'Ronspot exchange';
        // Forward the company picked at discovery (multi-company domains) so a new user
        // is provisioned into the right company.
        const companyId = sessionStorage.getItem('kc_company_id') || '';
        const extra = companyId ? { companyId } : {};
        // Phase D1: forward the KC access token so the server can pull the brokered Microsoft
        // token and enrich the new user's identity properties (department/city) for team
        // assignment. Optional — the server simply skips enrichment if it's absent.
        if (kcTokens.access_token) extra.keycloak_access_token = kcTokens.access_token;
        const user = await exchangeKCTokenForRonspot(kcTokens.id_token, extra);
        sessionStorage.removeItem('kc_company_id');

        if (cancelled) return;

        persistSession({
          isSSOLogin: 1,
          accessToken: user.AccessToken,
          refreshToken: user.RefreshToken,
          guid: user.guid,
          loginId: String(user.id ?? ''),
          firstName: user.FirstName || '',
          lastName: user.LastName || '',
          email: user.PrimaryEmail || '',
        });

        // Parity with the legacy LoginEvent: drive the first-login "Attention required"
        // modal. Non-fatal — a failure here must not block sign-in.
        try {
          const vres = await CheckUserVehicle_api({ GuId: user.guid });
          const recs = vres?.Data?.Records;
          if (Number(recs?.ParkingZonesAssigned ?? 0) === 1 && Number(recs?.ShowVehiclePopup ?? 0) === 1) {
            localStorage.setItem('vehiclenotavailable', 'true');
          } else {
            localStorage.removeItem('vehiclenotavailable');
          }
        } catch (vErr) {
          console.error('KC callback vehicle check failed (non-fatal):', vErr);
        }

        window.history.replaceState({}, document.title, window.location.pathname);
        history.replace('/calendar');
      } catch (err) {
        if (!cancelled) {
          console.error('Keycloak sign-in failed:', err);
          setError({ step, message: err.message || 'Unknown error', details: err.details || null });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [history]);

  const centerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f3f3f3',
    fontFamily: 'inherit',
  };

  const cardStyle = {
    textAlign: 'center',
    background: '#fff',
    borderRadius: 16,
    padding: '40px 48px',
    boxShadow: '0 4px 28px rgba(0,0,0,0.09)',
  };

  if (error) {
    const detailsText =
      SHOW_DIAGNOSTICS && error.details && Object.keys(error.details).length
        ? JSON.stringify(error.details, null, 2)
        : null;

    return (
      <div style={centerStyle}>
        <div style={{ ...cardStyle, textAlign: SHOW_DIAGNOSTICS ? 'left' : 'center', maxWidth: 560, width: '90vw' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={Images.RonspotSquareLogo} alt="Ronspot" style={{ width: 48, marginBottom: 16 }} />
          </div>
          <p style={{ color: '#ef3826', fontSize: 15, fontWeight: 600, margin: '0 0 4px', textAlign: 'center' }}>
            Sign-in failed
          </p>

          {SHOW_DIAGNOSTICS ? (
            <>
              <p style={{ color: '#666', fontSize: 12, margin: '0 0 12px' }}>
                Diagnostic details (staging)
              </p>

              <div style={{ fontSize: 13, color: '#333', marginBottom: 10 }}>
                <div><strong>Failed at:</strong> {error.step}</div>
                <div style={{ marginTop: 4 }}><strong>Message:</strong> {error.message}</div>
              </div>

              {detailsText && (
                <pre
                  style={{
                    background: '#1e1e1e',
                    color: '#d4d4d4',
                    borderRadius: 8,
                    padding: '12px 14px',
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: '0 0 16px',
                  }}
                >
                  {detailsText}
                </pre>
              )}
            </>
          ) : (
            <p style={{ color: '#666', fontSize: 13, margin: '0 0 16px', textAlign: 'center' }}>
              We couldn&apos;t sign you in. Please try again, or use the main login page.
            </p>
          )}

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => history.replace('/kc-login')}
              style={{
                color: '#fff',
                background: '#01a08c',
                border: 'none',
                borderRadius: 30,
                padding: '10px 28px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={centerStyle}>
      <div style={cardStyle}>
        <img src={Images.RonspotSquareLogo} alt="Ronspot" style={{ width: 48, marginBottom: 20 }} />
        <img src={Images.LoaderFull} alt="Loading…" style={{ width: 40, display: 'block', margin: '0 auto 12px' }} />
        <p style={{ color: '#888', fontSize: 14, margin: 0 }}>Signing you in…</p>
      </div>
    </div>
  );
}
